import type {
  Expr,
  ExprCmp,
  ExprCondition,
  ExprOp,
  PatternAggregate,
  PatternBranch,
  PatternMeasure,
  PatternScope,
} from '@kkp-fraud/shared';
import { isExpressionRow } from './branches';
import { formatClauses, formatWindow } from './format';
import { t } from './i18n';

/**
 * 2a expressions — the console side of an EXPRESSION row (docs/engine-rows-semantics.md §7):
 * a pattern action row whose named measures ("counters") feed `conditions`. The engine alone
 * decides what is valid (its problems are shown as they come); this module only builds,
 * converts and READS BACK such a row, in the active language.
 */

/** The scopes a counter of an expression row may use (the rule level keeps the older three). */
export const EXPR_SCOPES: readonly PatternScope[] = ['card_id', 'customer_id', 'merchant_id', 'terminal_id'];
/** The aggregates a counter of an expression row may use. */
export const EXPR_AGGREGATES: readonly PatternAggregate[] = ['count', 'sum', 'distinct', 'avg', 'min', 'max', 'last', 'seconds_since_last', 'streak'];
/** Aggregates that read a field (`aggregateField`), and the ones that need it to be a number. */
export const FIELD_AGGREGATES: ReadonlySet<PatternAggregate> = new Set(['sum', 'distinct', 'avg', 'min', 'max', 'last']);
export const NUMERIC_AGGREGATES: ReadonlySet<PatternAggregate> = new Set(['sum', 'avg', 'min', 'max']);
/** Aggregates the engine only accepts with `includeCurrent: false` (the current transaction would always be newest). */
export const HISTORY_ONLY_AGGREGATES: ReadonlySet<PatternAggregate> = new Set(['last', 'seconds_since_last']);
export const EXPR_CMPS: readonly ExprCmp[] = ['gte', 'gt', 'lte', 'lt', 'eq', 'neq', 'between'];
export const EXPR_OPS: readonly ExprOp[] = ['add', 'sub', 'mul', 'div', 'min', 'max', 'abs', 'mod'];
/** Operators nested in one side at most (the engine's limit). */
export const MAX_EXPR_DEPTH = 4;
/** Counters in one expression row at most (the engine's limit). */
export const MAX_EXPR_MEASURES = 8;

/** Operand counts per operator: [min, max]. */
export function arity(op: ExprOp): [number, number] {
  if (op === 'abs') return [1, 1];
  if (op === 'mod') return [2, 2];
  return [2, 4];
}

/** The comparison as a symbol (the builder's dropdown). */
export const CMP_SYMBOL: Record<ExprCmp, string> = { gt: '>', gte: '≥', lt: '<', lte: '≤', eq: '=', neq: '≠', between: '…' };
/** The operator as a symbol (the builder's dropdown). */
export const OP_SYMBOL: Record<ExprOp, string> = { add: '+', sub: '−', mul: '×', div: '÷', min: 'min', max: 'max', abs: 'abs', mod: 'mod' };

// ---- shape helpers -------------------------------------------------------------------

export type OperandKind = 'm' | 'field' | 'num' | 'text' | 'bool' | 'calc';

export function operandKind(e: Expr | undefined): OperandKind {
  if (typeof e === 'number') return 'num';
  if (typeof e === 'string') return 'text';
  if (typeof e === 'boolean') return 'bool';
  if (e && typeof e === 'object') {
    if ('m' in e) return 'm';
    if ('field' in e) return 'field';
    if ('op' in e) return 'calc';
  }
  return 'num';
}

/** The measure names of a row, in order (a counter without a name is skipped). */
export function measureNames(row: PatternBranch): string[] {
  return row.measures.map((m) => m.name).filter((n): n is string => typeof n === 'string' && n !== '');
}

/** `m1`, `m2`, … — the first name not taken. */
export function nextMeasureName(taken: readonly string[]): string {
  for (let i = 1; ; i++) if (!taken.includes(`m${i}`)) return `m${i}`;
}

/** Every `{ m: from }` in `e` renamed to `to`. */
export function renameInExpr(e: Expr, from: string, to: string): Expr {
  if (e && typeof e === 'object') {
    if ('m' in e) return e.m === from ? { m: to } : e;
    if ('op' in e) return { op: e.op, args: e.args.map((a) => renameInExpr(a, from, to)) };
  }
  return e;
}

export function renameInConditions(conditions: readonly ExprCondition[], from: string, to: string): ExprCondition[] {
  return conditions.map((c) => ({
    ...c,
    left: renameInExpr(c.left, from, to),
    right: Array.isArray(c.right)
      ? [renameInExpr(c.right[0], from, to), renameInExpr(c.right[1], from, to)]
      : renameInExpr(c.right as Expr, from, to),
  }));
}

/** Every transaction field an expression row's conditions read (`{ field }`). */
export function conditionFields(row: PatternBranch): string[] {
  const out = new Set<string>();
  const walk = (e: Expr | undefined) => {
    if (!e || typeof e !== 'object') return;
    if ('field' in e) out.add(String(e.field));
    if ('op' in e) e.args.forEach(walk);
  };
  for (const c of row.conditions ?? []) {
    walk(c.left);
    if (Array.isArray(c.right)) c.right.forEach(walk);
    else walk(c.right as Expr);
  }
  return [...out];
}

/**
 * A threshold row as an expression row: each counter named (`m1`, `m2`, … unless it has a
 * name), its threshold moved into a condition `mN ≥ threshold`, the conditions joined by the
 * row's AND/OR. Evaluates exactly as the threshold row did (a missing measure is unmet in both).
 */
export function toExpressionRow(row: PatternBranch): PatternBranch {
  const taken: string[] = [];
  const measures: PatternMeasure[] = row.measures.map((m) => {
    const name = m.name && !taken.includes(m.name) ? m.name : nextMeasureName(taken);
    taken.push(name);
    const next: PatternMeasure = { ...m, name };
    delete next.threshold;
    return next;
  });
  const conditions: ExprCondition[] = row.measures.map((m, j) => {
    const c: ExprCondition = { left: { m: taken[j]! }, cmp: 'gte', right: Number.isFinite(m.threshold) ? (m.threshold as number) : 1 };
    if (j > 0 && row.logic === 'OR') c.logic = 'OR';
    return c;
  });
  return { action: row.action, measures, conditions };
}

/**
 * An expression row back as a threshold row — only when that is EXACT: one condition per
 * counter, in order, each `name ≥ number`, all joined by the same AND or OR, and every counter
 * in the older vocabulary (no calendar, no includeCurrent, scope and aggregate a threshold row
 * may use). Otherwise `null` (the editor then keeps the row advanced).
 */
export function toThresholdRow(row: PatternBranch): PatternBranch | null {
  const conditions = row.conditions ?? [];
  if (conditions.length !== row.measures.length || conditions.length === 0) return null;
  const joins = new Set(conditions.slice(1).map((c) => c.logic ?? 'AND'));
  if (joins.size > 1) return null;
  const measures: PatternMeasure[] = [];
  for (const [j, m] of row.measures.entries()) {
    const c = conditions[j]!;
    const left = c.left;
    if (c.cmp !== 'gte' || typeof c.right !== 'number' || !left || typeof left !== 'object' || !('m' in left) || left.m !== m.name) return null;
    if (m.calendar !== undefined || m.includeCurrent !== undefined) return null;
    if (!['card_id', 'customer_id', 'merchant_id'].includes(m.scope) || !['count', 'sum', 'distinct'].includes(m.aggregate)) return null;
    const next: PatternMeasure = { ...m, threshold: c.right };
    delete next.name;
    measures.push(next);
  }
  const out: PatternBranch = { action: row.action, measures };
  if (joins.has('OR')) out.logic = 'OR';
  return out;
}

// ---- the readback ---------------------------------------------------------------------

const NUMBER = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 6 });

function literal(e: Expr): string {
  if (typeof e === 'number') return Number.isFinite(e) ? NUMBER.format(e) : '…';
  if (typeof e === 'string') return `'${e}'`;
  return String(e);
}

/** One counter as words: "number of approved transactions where amount_thb > 50000 in 24 h". */
export function measureText(m: PatternMeasure): string {
  const event = t(`event.${m.event}.label`);
  const field = String(m.aggregateField ?? '?');
  let text = t(`expr.m.${m.aggregate}`, { event, field });
  if (m.scope !== 'card_id') text = t('expr.m.scope', { what: text, scope: m.scope });
  if ((m.filters ?? []).length > 0) text = t('expr.m.where', { what: text, filters: formatClauses(m.filters) });
  text = m.calendar ? t(`expr.m.calendar.${m.calendar}`, { what: text }) : t('expr.m.window', { what: text, window: formatWindow(m.windowSeconds) });
  if (m.includeCurrent === false) text = t('expr.m.exclude_current', { what: text });
  return text;
}

const INFIX: ReadonlySet<ExprOp> = new Set(['add', 'sub', 'mul', 'div', 'mod']);

function operandText(e: Expr | undefined, measures: readonly PatternMeasure[], nested: boolean): string {
  if (e === undefined || e === null) return '…';
  if (typeof e !== 'object') return literal(e);
  if ('m' in e) {
    const m = measures.find((x) => x.name === e.m);
    if (!m) return e.m;
    const words = measureText(m);
    return nested && (m.filters ?? []).length > 0 ? `(${words})` : words;
  }
  if ('field' in e) return t('expr.field', { field: String(e.field) });
  const args = (e.args ?? []).map((a) => {
    const inner = a && typeof a === 'object' && 'op' in a && INFIX.has(a.op) && INFIX.has(e.op);
    const text = operandText(a, measures, true);
    return inner ? `(${text})` : text;
  });
  if (INFIX.has(e.op)) return args.join(` ${t(`expr.op.${e.op}`)} `);
  return t(`expr.op.${e.op}`, { args: args.join(', ') });
}

/** One condition as words: "… plus … is at least 10". */
export function conditionText(c: ExprCondition, measures: readonly PatternMeasure[]): string {
  // a side on its own needs no brackets; a counter inside a calculation does (see operandText)
  const left = operandText(c.left, measures, false);
  if (c.cmp === 'between') {
    const [lo, hi] = Array.isArray(c.right) ? c.right : [undefined, undefined];
    // low and high are joined by "and": a counter with filters there is bracketed, as in a calculation
    return t('expr.cmp.between', { left, lo: operandText(lo, measures, true), hi: operandText(hi, measures, true) });
  }
  const right = operandText(Array.isArray(c.right) ? c.right[0] : c.right, measures, false);
  return `${left} ${t(`expr.cmp.${c.cmp}`)} ${right}`;
}

/** An expression row as one line of words, its conditions joined left to right by and / or. */
export function expressionRowText(row: PatternBranch): string {
  const conditions = row.conditions ?? [];
  if (conditions.length === 0) return t('expr.readback.empty');
  return conditions
    .map((c, i) => (i === 0 ? conditionText(c, row.measures) : `${t(`expr.join.${c.logic === 'OR' ? 'OR' : 'AND'}`)} ${conditionText(c, row.measures)}`))
    .join(' ');
}

// ---- the code line (the engine's own notation, as its evidence strings print it) --------

const CODE_OP: Record<ExprOp, string> = { add: '+', sub: '-', mul: '*', div: '/', mod: '%', min: 'min', max: 'max', abs: 'abs' };
const CODE_CMP: Record<ExprCmp, string> = { gt: '>', gte: '>=', lt: '<', lte: '<=', eq: '==', neq: '!=', between: 'between' };

export function exprCode(e: Expr | undefined): string {
  if (e === undefined || e === null) return '?';
  if (typeof e !== 'object') return typeof e === 'string' ? `'${e}'` : String(e);
  if ('m' in e) return e.m;
  if ('field' in e) return String(e.field);
  const args = (e.args ?? []).map((a) => {
    const text = exprCode(a);
    return a && typeof a === 'object' && 'op' in a && INFIX.has(a.op) && INFIX.has(e.op) ? `(${text})` : text;
  });
  return INFIX.has(e.op) ? args.join(` ${CODE_OP[e.op]} `) : `${CODE_OP[e.op]}(${args.join(', ')})`;
}

/** `big + shopA >= 10 AND …` */
export function expressionRowCode(row: PatternBranch): string {
  return (row.conditions ?? [])
    .map((c, i) => {
      const body =
        c.cmp === 'between' && Array.isArray(c.right)
          ? `${exprCode(c.left)} between ${exprCode(c.right[0])} and ${exprCode(c.right[1])}`
          : `${exprCode(c.left)} ${CODE_CMP[c.cmp]} ${exprCode(Array.isArray(c.right) ? c.right[0] : c.right)}`;
      return i === 0 ? body : `${c.logic === 'OR' ? 'OR' : 'AND'} ${body}`;
    })
    .join(' ');
}

export { isExpressionRow };
